import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Formik, Field } from 'formik'
import { MagentoCountryCodeEnum } from 'lib/types'
import { CustomerAddress, AddressInput } from 'lib/types/core'
import { DEFAULT_COUNTRY_CODE } from 'lib/core/resources/constants'
import { Button, Form, Grid } from 'lib/ui/components'
import { useDirectoryOptions } from 'lib/core/hooks'
import { schema, Values, addressValidation } from './schema'
import { MakeDefault } from 'lib/shared/components/CustomerStyles'
import { DISABLED_REGIONS } from 'hooks/useShippingRegionOptions'

interface Props {
  address?: CustomerAddress
  onSubmit: (event: { address: AddressInput }) => void
  isFetching: boolean
}

export const AddressForm = ({
  address,
  onSubmit,
  isFetching
}: Props) => {
  const intl = useIntl()
  const [countryCode, setCountryCode] = useState<string>(address?.countryCode ? address.countryCode : DEFAULT_COUNTRY_CODE)
  const [isDefaultBilling, setIsDefaultBilling] = useState(address?.isDefaultBilling ?? false)
  const [isDefaultShipping, setIsDefaultShipping] = useState(address?.isDefaultShipping ?? false)

  const {
    countryOptions,
    countryOptionsIsFetching,
    regionOptions,
    countryInfoIsFetching
  } = useDirectoryOptions({ countryCode })

  const [initialValues] = useState({
    ...schema,
    ...address,
    countryCode
  })

  const handleCountryChange = ({ value, name, form }: any) => { // TODO: Type
    const { setFieldValue, setFieldTouched } = form
    setCountryCode(value)
    setFieldValue('regionCode', '', false)
    setFieldTouched('regionCode', false, false)
  }

  const handleDefaultShippingChange = () => {
    setIsDefaultShipping(!isDefaultShipping)
  }

  const handleDefaultBillingChange = () => {
    setIsDefaultBilling(!isDefaultBilling)
  }

  const handleSubmit = (values: Values) => {
    const {
      firstName,
      lastName,
      regionCode,
      countryCode,
      street,
      street2,
      city,
      postalCode,
      phoneNumber
    } = values

    const region = regionOptions.find(option => option.value === regionCode)
    const country = countryOptions.find(option => option.value === countryCode)
    const address: AddressInput = {
      city,
      countryCode: country?.value as MagentoCountryCodeEnum,
      firstName,
      lastName,
      postalCode,
      region: region?.label as string,
      regionCode: region?.value as string,
      regionId: region?.id as number,
      street,
      street2,
      phoneNumber,
      isDefaultShipping,
      isDefaultBilling,
      company: ''
    }

    onSubmit({
      address
    })
  }

  return (
    <>
      <Formik
        enableReinitialize
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={addressValidation({ intl })}
      >
        {(formikProps: any) => {
          const { setFieldValue, setFieldTouched, values, isValid } = formikProps
          const shippingDefaultDisable = DISABLED_REGIONS.includes(values.regionCode)
          shippingDefaultDisable && setIsDefaultShipping(false)

          return (
            <Form.Wrapper>
              <Grid>
                <div>
                  <Field
                    name='firstName'
                    label={intl.formatMessage({
                      id: 'checkout.shipping.label.firstName'
                    })}
                    component={Form.Input}
                    hasAutoPlaceholder
                    isRequired
                  />
                </div>
                <div>
                  <Field
                    name='lastName'
                    label={intl.formatMessage({
                      id: 'checkout.shipping.label.lastName'
                    })}
                    component={Form.Input}
                    hasAutoPlaceholder
                    isRequired
                  />
                </div>
              </Grid>

              <Field
                name='phoneNumber'
                label={intl.formatMessage({
                  id: 'checkout.shipping.label.phoneNumber'
                })}
                component={Form.Input}
                hasAutoPlaceholder
                isRequired
              />

              <Field
                name='street'
                label={intl.formatMessage({
                  id: 'checkout.shipping.label.street'
                })}
                component={Form.Input}
                hasAutoPlaceholder
                isRequired
              />

              <Field
                name='street2'
                label={intl.formatMessage({
                  id: 'checkout.shipping.label.street2'
                })}
                component={Form.Input}
                hasAutoPlaceholder
              />

              <Grid>
                <div>
                  <Field
                    name='city'
                    label={intl.formatMessage({
                      id: 'checkout.shipping.label.city'
                    })}
                    component={Form.Input}
                    hasAutoPlaceholder
                    isRequired
                  />
                </div>
                <div>
                  <Field
                    name='regionCode'
                    label={intl.formatMessage({
                      id: 'checkout.shipping.label.region'
                    })}
                    component={Form.Select}
                    options={regionOptions}
                    isLoading={countryInfoIsFetching}
                    isRequired
                  />
                </div>
                <div>
                  <Field
                    name='postalCode'
                    label={intl.formatMessage({
                      id: 'checkout.shipping.label.postalCode'
                    })}
                    component={Form.Input}
                    hasAutoPlaceholder
                    isRequired
                  />
                </div>
              </Grid>

              <Field
                name='countryCode'
                label={intl.formatMessage({
                  id: 'checkout.shipping.label.country'
                })}
                component={Form.Select}
                options={countryOptions}
                interceptChange={handleCountryChange}
                isLoading={countryOptionsIsFetching}
                isRequired
              />
              {!shippingDefaultDisable ? (
                <MakeDefault>
                  <label>
                    <Field
                      name='defaultShipping'
                      type='checkbox'
                      disabled={isFetching}
                      checked={isDefaultShipping}
                      onChange={() => {
                        handleDefaultShippingChange()
                        setFieldValue('defaultShipping', !values.defaultShipping, true)
                        setFieldTouched('defaultShipping', true, true)
                      }}
                    />
                    <span />
                    {intl.formatMessage({
                      id: 'customer.account.address.makeDefaultShippingLabel'
                    })}
                  </label>
                </MakeDefault>
              ) : (
                <p><strong>Shipping is unavailable to location selected.</strong></p>
              )}
              <MakeDefault>
                <label>
                  <Field
                    name='defaultBilling'
                    type='checkbox'
                    disabled={isFetching}
                    checked={isDefaultBilling}
                    onChange={handleDefaultBillingChange}
                  />
                  <span />
                  {intl.formatMessage({
                    id: 'customer.account.address.makeDefaultBillingLabel'
                  })}
                </label>
              </MakeDefault>
              <Form.Actions.Wrapper>
                <Button
                  type='submit'
                  isLoading={isFetching}
                  isDisabled={!isValid}
                >
                  Save
                </Button>
              </Form.Actions.Wrapper>
            </Form.Wrapper>
          )
        }}
      </Formik>
    </>
  )
}
